import * as THREE from 'three';
import { cheapLighting, litMaterial } from '../core/materials';
import { PROP_RADIUS, propModel } from '../entities/models';
import type { StageDef } from '../game/content';
import { rgb } from '../utils/color';
import { createSeededRandom } from '../utils/random';
import type { Solid } from './Enemies';
import type { Particles } from './Fx';

const TILE = 20; // world units covered by one ground texture repeat (sized for the high camera: ~1 texel per screen pixel)
const FIELD = 64; // props repeat every FIELD units in x and z

interface PropInstance {
  kind: string;
  ox: number;
  oz: number;
  rot: number;
  scale: number;
  solid: boolean;
  idx: number;
  x: number; // where its matrix was last written
  z: number;
}

const AMBIENT_RATE = { none: 0, embers: 22, snow: 40, motes: 10, rain: 60 } as const;

// Endless stage scenery: ground that follows the player, wrapping props, lights and ambient particles.
export class Environment {
  readonly group = new THREE.Group();
  readonly solids: Solid[] = [];
  private ground: THREE.Mesh | null = null;
  private readonly hemi = new THREE.HemisphereLight('#ffffff', '#222222', 1);
  readonly sun = new THREE.DirectionalLight('#ffffff', 1.5);
  private readonly fill = new THREE.AmbientLight('#ffffff', 0.15);
  private props: PropInstance[] = [];
  private readonly propMeshes = new Map<string, { body: THREE.InstancedMesh; glow: THREE.InstancedMesh | null; dirty: boolean }>();
  private stage: StageDef | null = null;
  private emitAcc = 0;
  private flash = 0;
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly posV = new THREE.Vector3();
  private readonly scaleV = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly tmp = new THREE.Color();
  private readonly accent = new THREE.Color();

  constructor(private readonly scene: THREE.Scene) {
    this.sun.castShadow = true;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 80;
    const s = this.sun.shadow.camera;
    // Covers the whole view, which reaches ~34 units from the hero at the top corners.
    s.left = -24;
    s.right = 24;
    s.top = 24;
    s.bottom = -24;



    this.sun.shadow.bias = -0.0008;
    this.sun.shadow.normalBias = 0.02;
    this.group.add(this.hemi, this.sun, this.sun.target, this.fill);
  }

  setShadowSize(size: number): void {
    if (this.sun.shadow.mapSize.x === size && this.sun.shadow.map) return;
    this.sun.shadow.mapSize.set(size, size);
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
  }

  build(stage: StageDef, stageIndex: number, shadowSize: number): void {
    this.dispose();
    this.stage = stage;
    const v = stage.visuals;
    this.scene.background = new THREE.Color(v.sky);
    // Stage fog was tuned for a camera ~22 units from the hero with a 42° FOV; it now sits ~29 away at 48° (Game CAMERA_OFFSET).
    this.scene.fog = new THREE.Fog(v.fog, v.fogNear + 8, v.fogFar + 8);
    this.hemi.color.set(v.hemiSky);
    this.hemi.groundColor.set(v.hemiGround);
    this.hemi.intensity = v.hemi;
    this.sun.color.set(v.sun);
    this.sun.intensity = v.sunIntensity;
    this.fill.color.set(v.hemiSky);
    this.setShadowSize(shadowSize);
    const glow = v.cracks ?? v.accent;
    this.accent.set(glow);

    const { map, emissive } = groundTextures(stage, stageIndex);
    const size = 150;
    for (const t of [map, emissive]) {
      if (!t) continue;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(size / TILE, size / TILE);
      t.anisotropy = cheapLighting() ? 2 : 8; // the ground fills the screen: 8x filtering is a real cost for phone GPUs
    }
    map.colorSpace = THREE.SRGBColorSpace;
    const material = litMaterial({
      map,
      roughness: v.detail === 'snow' ? 0.65 : 0.92,
      metalness: 0,
      emissive: emissive ? new THREE.Color(glow) : new THREE.Color('#000000'),
      emissiveMap: emissive,
      emissiveIntensity: emissive ? 1.2 : 0,
    });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2), material);
    this.ground.receiveShadow = true;
    this.group.add(this.ground);

    const rng = createSeededRandom(1000 + stageIndex * 77);
    this.props = [];
    for (const { kind, count, solid } of v.props) {
      let placed = 0;
      for (let i = 0; i < count; i += 1) {
        const ox = rng() * FIELD;
        const oz = rng() * FIELD;
        // keep a clearing around the spawn point (world origin is a lattice corner)
        if (Math.hypot(Math.min(ox, FIELD - ox), Math.min(oz, FIELD - oz)) < 4) continue;
        this.props.push({ kind, ox, oz, rot: rng() * Math.PI * 2, scale: 0.8 + rng() * 0.55, solid, idx: placed++, x: NaN, z: NaN });
      }
      const model = propModel(kind);
      const body = new THREE.InstancedMesh(model.body, litMaterial({ vertexColors: true, flatShading: true, roughness: 0.85 }), Math.max(1, placed));
      body.count = placed;
      body.castShadow = solid;
      body.receiveShadow = true;
      body.frustumCulled = false;
      let glowMesh: THREE.InstancedMesh | null = null;
      if (model.glow) {
        const intensity = kind === 'candle' ? 0.85 : kind === 'crystal' ? 1.35 : 1.9;
        glowMesh = new THREE.InstancedMesh(model.glow, new THREE.MeshBasicMaterial({ color: this.tmp.copy(this.accent).multiplyScalar(intensity) }), Math.max(1, placed));
        glowMesh.count = placed;
        glowMesh.frustumCulled = false;
        this.group.add(glowMesh);
      }
      this.group.add(body);
      this.propMeshes.set(kind, { body, glow: glowMesh, dirty: true });
    }
    this.update(0, 0, 0, null);
  }

  lightning(): void {
    this.flash = 1;
  }

  update(dt: number, px: number, pz: number, particles: Particles | null, rng?: () => number): void {
    if (!this.stage || !this.ground) return;
    this.ground.position.set(Math.round(px / TILE) * TILE, 0, Math.round(pz / TILE) * TILE);
    this.sun.position.set(px - 10, 22, pz + 12);
    this.sun.target.position.set(px, 0, pz);
    this.flash = Math.max(0, this.flash - dt * 3);
    this.hemi.intensity = this.stage.visuals.hemi * (1 + this.flash * 2.5);

    // Props only move when they wrap to the player's side of the field (shards also bob and spin), so their
    // matrices are rewritten and re-uploaded only then; solids reuse their objects (no per-frame garbage).
    let solids = 0;
    for (const p of this.props) {
      const x = p.ox + FIELD * Math.round((px - p.ox) / FIELD);
      const z = p.oz + FIELD * Math.round((pz - p.oz) / FIELD);
      if (x !== p.x || z !== p.z) {
        const meshes = this.propMeshes.get(p.kind)!;
        this.posV.set(x, 0, z);
        this.quat.setFromAxisAngle(this.up, p.rot);
        this.scaleV.setScalar(p.scale);
        this.matrix.compose(this.posV, this.quat, this.scaleV);
        meshes.body.setMatrixAt(p.idx, this.matrix);
        meshes.glow?.setMatrixAt(p.idx, this.matrix);
        meshes.dirty = true;
        p.x = x;
        p.z = z;
      }
      if (p.solid && Math.abs(x - px) < 28 && Math.abs(z - pz) < 28) {
        const s = (this.solids[solids++] ??= { x: 0, z: 0, r: 0 });
        s.x = x;
        s.z = z;
        s.r = (PROP_RADIUS[p.kind] ?? 0.5) * p.scale;
      }
    }
    this.solids.length = solids;
    for (const m of this.propMeshes.values()) {
      if (!m.dirty) continue;
      m.dirty = false;
      m.body.instanceMatrix.needsUpdate = true;
      if (m.glow) m.glow.instanceMatrix.needsUpdate = true;
    }
    if (particles && rng && dt > 0) this.ambient(dt, px, pz, particles, rng);
  }

  private ambient(dt: number, px: number, pz: number, particles: Particles, rng: () => number): void {
    const v = this.stage!.visuals;
    this.emitAcc += AMBIENT_RATE[v.ambient] * dt;
    const c = this.tmp;
    while (this.emitAcc >= 1) {
      this.emitAcc -= 1;
      const x = px + (rng() - 0.5) * 40;
      const z = pz + (rng() - 0.5) * 30 - 3;
      switch (v.ambient) {
        case 'embers':
          particles.emit(x, 0.05, z, (rng() - 0.5) * 0.6, 1 + rng() * 1.5, (rng() - 0.5) * 0.6, c.copy(rgb('#ff7a2f')).multiplyScalar(2.5), 0.22, 2.2, 0.3, 0.3);
          break;
        case 'snow':
          particles.emit(x - 4, 8, z, 1.2 + rng(), -2.2, 0.4, c.setRGB(1.1, 1.15, 1.25), 0.16, 4, 0, 0);
          break;
        case 'motes':
          particles.emit(x, 0.2 + rng() * 1.5, z, 0, 0.25, 0, c.copy(rgb(rng() < 0.5 ? '#7dffb0' : '#b38bff')).multiplyScalar(1.6), 0.25, 3, 0, 0);
          break;
        case 'rain':
          particles.emit(x + 3, 10, z, -3, -26, 0, c.setRGB(0.55, 0.65, 0.85), 0.09, 0.42, 0, 0);
          break;
      }
    }
    if (v.ambient === 'rain' && rng() < dt * 0.12) this.lightning();
  }

  dispose(): void {
    if (this.ground) {
      this.group.remove(this.ground);
      this.ground.geometry.dispose();
      const m = this.ground.material as THREE.MeshStandardMaterial | THREE.MeshLambertMaterial;
      m.map?.dispose();
      m.emissiveMap?.dispose();
      m.dispose();
      this.ground = null;
    }
    for (const m of this.propMeshes.values()) {
      for (const mesh of [m.body, m.glow]) {
        if (!mesh) continue;
        this.group.remove(mesh);
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
    }
    this.propMeshes.clear();
    this.solids.length = 0;
  }
}

// --- procedural ground textures (seamless: every stroke is drawn with wrap-around copies) ---

function groundTextures(stage: StageDef, stageIndex: number): { map: THREE.CanvasTexture; emissive: THREE.CanvasTexture | null } {
  const S = 512;
  const rng = createSeededRandom(42 + stageIndex * 13);
  const v = stage.visuals;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const g = canvas.getContext('2d')!;
  g.fillStyle = v.ground[0];
  g.fillRect(0, 0, S, S);

  const wrap = (draw: (ox: number, oy: number) => void) => {
    for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) draw(ox, oy);
  };
  const blob = (x: number, y: number, r: number, color: string, alpha: number) => {
    wrap((ox, oy) => {
      const grad = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      grad.addColorStop(0, hexA(color, alpha));
      grad.addColorStop(1, hexA(color, 0));
      g.fillStyle = grad;
      g.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
    });
  };

  for (let i = 0; i < 40; i += 1) blob(rng() * S, rng() * S, 40 + rng() * 110, rng() < 0.5 ? v.ground[1] : v.ground[2], 0.55);
  for (let i = 0; i < 1400; i += 1) {
    const x = rng() * S;
    const y = rng() * S;
    g.fillStyle = hexA(rng() < 0.5 ? v.ground[1] : v.ground[2], 0.5);
    g.fillRect(x, y, 2, 2);
  }

  switch (v.detail) {
    case 'grass':
      for (let i = 0; i < 2600; i += 1) {
        const x = rng() * S;
        const y = rng() * S;
        g.strokeStyle = hexA(rng() < 0.6 ? '#3f7a35' : '#1a3a1c', 0.55);
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + (rng() - 0.5) * 4, y - 3 - rng() * 5);
        g.stroke();
      }
      for (let i = 0; i < 30; i += 1) blob(rng() * S, rng() * S, 6 + rng() * 10, '#6b5a3a', 0.35);
      break;
    case 'ash':
    case 'rock':
    case 'stone':
      for (let i = 0; i < 160; i += 1) {
        const x = rng() * S;
        const y = rng() * S;
        const r = 3 + rng() * 9;
        wrap((ox, oy) => {
          g.fillStyle = hexA(v.ground[rng() < 0.5 ? 1 : 2], 0.9);
          g.beginPath();
          for (let k = 0; k < 6; k += 1) {
            const a = (k / 6) * Math.PI * 2;
            const rr = r * (0.7 + rng() * 0.5);
            g.lineTo(x + ox + Math.cos(a) * rr, y + oy + Math.sin(a) * rr);
          }
          g.fill();
        });
      }
      if (v.detail === 'stone') {
        g.strokeStyle = hexA('#0b0a10', 0.55);
        g.lineWidth = 2;
        for (let y = 0; y <= S; y += 64) {
          g.beginPath();
          g.moveTo(0, y);
          g.lineTo(S, y);
          g.stroke();
          for (let x = (y / 64) % 2 ? 32 : 0; x <= S; x += 64) {
            g.beginPath();
            g.moveTo(x, y);
            g.lineTo(x, y + 64);
            g.stroke();
          }
        }
      }
      break;
    case 'snow':
      for (let i = 0; i < 50; i += 1) blob(rng() * S, rng() * S, 20 + rng() * 50, '#ffffff', 0.35);
      for (let i = 0; i < 600; i += 1) {
        g.fillStyle = 'rgba(255,255,255,0.9)';
        g.fillRect(rng() * S, rng() * S, 1, 1);
      }
      for (let i = 0; i < 20; i += 1) blob(rng() * S, rng() * S, 10 + rng() * 20, '#7d95a8', 0.3);
      break;
    case 'sand':
      // Wind ripples and pale grains.
      for (let i = 0; i < 420; i += 1) {
        const x = rng() * S;
        const y = rng() * S;
        const w = 10 + rng() * 26;
        g.strokeStyle = hexA(rng() < 0.5 ? v.ground[1] : v.ground[2], 0.5);
        g.lineWidth = 1.5;
        wrap((ox, oy) => {
          g.beginPath();
          g.moveTo(x + ox, y + oy);
          g.quadraticCurveTo(x + ox + w / 2, y + oy - 3, x + ox + w, y + oy);
          g.stroke();
        });
      }
      for (let i = 0; i < 700; i += 1) {
        g.fillStyle = 'rgba(255,240,210,0.6)';
        g.fillRect(rng() * S, rng() * S, 1, 1);
      }
      break;
  }

  let emissive: THREE.CanvasTexture | null = null;
  if (v.cracks) {
    const ec = document.createElement('canvas');
    ec.width = ec.height = S;
    const eg = ec.getContext('2d')!;
    eg.fillStyle = '#000000';
    eg.fillRect(0, 0, S, S);
    const cracks = v.detail === 'stone' ? 10 : 18;
    for (let i = 0; i < cracks; i += 1) {
      let x = rng() * S;
      let y = rng() * S;
      let a = rng() * Math.PI * 2;
      const pts: Array<[number, number]> = [[x, y]];
      const steps = 6 + Math.floor(rng() * 10);
      for (let k = 0; k < steps; k += 1) {
        a += (rng() - 0.5) * 1.2;
        x += Math.cos(a) * (8 + rng() * 14);
        y += Math.sin(a) * (8 + rng() * 14);
        pts.push([x, y]);
      }
      const width = 1 + rng() * 2.5;
      for (const [ox, oy] of [[-S, -S], [-S, 0], [-S, S], [0, -S], [0, 0], [0, S], [S, -S], [S, 0], [S, S]]) {
        for (const [lw, alpha] of [[width * 4, 0.12], [width * 2, 0.3], [width, 1]] as const) {
          eg.strokeStyle = `rgba(255,255,255,${alpha})`;
          eg.lineWidth = lw;
          eg.lineCap = 'round';
          eg.beginPath();
          pts.forEach(([px, py], k) => (k ? eg.lineTo(px + ox, py + oy) : eg.moveTo(px + ox, py + oy)));
          eg.stroke();
        }
      }
      // darken the crack in the albedo too
      g.strokeStyle = 'rgba(0,0,0,0.5)';
      g.lineWidth = width + 1;
      g.beginPath();
      pts.forEach(([px, py], k) => (k ? g.lineTo(px, py) : g.moveTo(px, py)));
      g.stroke();
    }
    emissive = new THREE.CanvasTexture(ec);
  }
  return { map: new THREE.CanvasTexture(canvas), emissive };
}

// Canvas colours stay in sRGB, so parse the hex directly (THREE.Color would linearise it).
function hexA(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}
